import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  AuthResponse,
  ChangePasswordDto,
  ErrorResponse,
  LoginDto,
  MessageResponse,
  RequestResetPasswordDto,
  ResetPasswordDto,
  TokenResponse,
  UserProfile,
} from './auth.models';

/** Chiavi per localStorage */
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  CURRENT_USER: 'currentUser',
} as const;

/**
 * AuthService - Servizio per la gestione dell'autenticazione utente
 *
 * Questo servizio gestisce:
 * - Login e logout degli utenti
 * - Gestione dei token JWT (access e refresh)
 * - Persistenza dello stato di autenticazione
 * - Reset e cambio password
 * - Validazione e scadenza dei token
 *
 * @injectable Il servizio è fornito globalmente
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // ========================================
  // PROPRIETÀ PRIVATE
  // ========================================

  /** Subject per lo stato di autenticazione reattivo */
  private readonly authStateSubject = new BehaviorSubject<boolean>(this.hasValidSession());

  /** Observable pubblico per lo stato di autenticazione */
  public readonly isAuthenticated$ = this.authStateSubject.asObservable();

  private readonly http = inject(HttpClient);

  private readonly router = inject(Router);

  /** URL base per le API di autenticazione */
  private readonly authUrl = `${environment.apiUrl}/auth`;

  /** Margine di sicurezza per la scadenza del token (millisecondi) */
  private readonly TOKEN_EXPIRY_MARGIN_MS = 30 * 1000; // 30 secondi

  /**
   * Restituisce l'access token corrente
   * @returns Il token di accesso o null se non presente
   */
  get currentAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Restituisce l'utente correntemente loggato
   * @returns Profilo utente o null se non presente
   */
  get currentUser(): UserProfile | null {
    const userJson = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    return userJson ? JSON.parse(userJson) : null;
  }

  /**
   * Restituisce lo stato corrente di autenticazione
   * @returns true se l'utente è autenticato, false altrimenti
   */
  get isAuthenticated(): boolean {
    return this.authStateSubject.value;
  }

  // ========================================
  // METODI DI AUTENTICAZIONE
  // ========================================

  /**
   * Cambia la password dell'utente corrente
   * @param changePasswordData Dati per il cambio password
   * @returns Observable con messaggio di conferma
   */
  changePassword(changePasswordData: ChangePasswordDto): Observable<MessageResponse> {
    return this.http.patch<MessageResponse>(`${this.authUrl}/change-password`, changePasswordData);
  }

  /**
   * Recupera il profilo dell'utente corrente
   * @returns Observable con il profilo utente
   */
  getCurrentUserProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${this.authUrl}/me`);
  }

  /**
   * Calcola il tempo rimanente prima della scadenza del token
   * @param token Token da verificare
   * @returns Millisecondi rimanenti prima della scadenza (0 se già scaduto)
   */
  getTimeUntilExpiration(token: string): number {
    const expirationDate = this.getTokenExpirationDate(token);
    if (!expirationDate) return 0;

    const now = new Date();
    return Math.max(0, expirationDate.getTime() - now.getTime());
  }

  /**
   * Estrae la data di scadenza dal token JWT
   * @param token Token JWT da decodificare
   * @returns Data di scadenza o null se non valida
   */
  getTokenExpirationDate(token: string): Date | null {
    try {
      const decoded = jwtDecode<{ exp?: number }>(token);
      if (decoded.exp) {
        // exp è in secondi, Date richiede millisecondi
        return new Date(decoded.exp * 1000);
      }
      return null;
    } catch (error) {
      console.error('Errore nella decodifica del token:', error);
      return null;
    }
  }

  // ========================================
  // GESTIONE PASSWORD
  // ========================================

  /**
   * Verifica se il token di accesso corrente è valido
   * @returns true se il token è presente e non scaduto
   */
  hasValidAccessToken(): boolean {
    const token = this.currentAccessToken;
    return token ? !this.isTokenExpired(token) : false;
  }

  /**
   * Inizializza il servizio verificando lo stato di autenticazione
   * Chiamare questo metodo nell'AppComponent
   */
  initializeAuth(): void {
    const hasValidSession = this.hasValidSession();
    this.updateAuthState(hasValidSession);

    if (!hasValidSession) {
      this.clearAuthData();
    }
  }

  /**
   * Verifica se il token è scaduto
   * @param token Token da verificare
   * @returns true se il token è scaduto o non valido
   */
  isTokenExpired(token: string): boolean {
    const expirationDate = this.getTokenExpirationDate(token);
    if (!expirationDate) return true;

    const now = new Date();
    return expirationDate.getTime() <= now.getTime() + this.TOKEN_EXPIRY_MARGIN_MS;
  }

  // ========================================
  // GESTIONE PROFILO UTENTE
  // ========================================

  /**
   * Effettua il login di un utente
   * @param loginData Credenziali di login
   * @returns Observable con la risposta di autenticazione
   */
  login(loginData: LoginDto): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.authUrl}/login`, { ...loginData, audience: 'admin' })
      .pipe(
        tap((response) => {
          this.handleAuthSuccess(response);
        }),
      );
  }

  // ========================================
  // GESTIONE TOKEN
  // ========================================

  /**
   * Effettua il logout dell'utente
   * @returns Observable con messaggio di conferma
   */
  logout(): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(`${this.authUrl}/logout`, {}).pipe(
      tap(() => {
        this.clearAuthData();
      }),
    );
  }

  /**
   * Logout locale (senza chiamata al server)
   * Utile in caso di errori di rete o token scaduti
   */
  logoutLocal(): void {
    this.clearAuthData();
    this.router.navigate(['/auth/login']);
  }

  /**
   * Rinnova l'access token utilizzando il refresh token
   * @returns Observable con i nuovi token
   */
  refreshToken(): Observable<TokenResponse> {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (!refreshToken) {
      throw new Error('Refresh token non disponibile');
    }

    return this.http
      .post<TokenResponse>(
        `${this.authUrl}/refresh`,
        {},
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        },
      )
      .pipe(
        tap((tokens) => {
          this.saveTokens(tokens);
        }),
      );
  }

  /**
   * Richiede il reset della password
   * @param requestData Email per il reset
   * @returns Observable con messaggio di conferma
   */
  requestResetPassword(requestData: RequestResetPasswordDto): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(`${this.authUrl}/request-reset-password`, requestData);
  }

  /**
   * Effettua il reset della password con token
   * @param resetPasswordData Dati per il reset con token
   * @returns Observable con messaggio di conferma o errore
   */
  resetPassword(resetPasswordData: ResetPasswordDto): Observable<MessageResponse | ErrorResponse> {
    return this.http.post<MessageResponse | ErrorResponse>(
      `${this.authUrl}/reset-password`,
      resetPasswordData,
    );
  }

  // ========================================
  // METODI DI UTILITÀ PRIVATI
  // ========================================

  /**
   * Aggiorna i campi del profilo utente in cache (localStorage)
   */
  updateCachedUser(updates: Partial<UserProfile>): void {
    const current = this.currentUser;
    if (current) {
      localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify({ ...current, ...updates }));
    }
  }

  /**
   * Cancella tutti i dati di autenticazione
   */
  private clearAuthData(): void {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
    this.updateAuthState(false);
  }

  /**
   * Gestisce il successo dell'autenticazione
   * @param response Risposta di autenticazione dal server
   */
  private handleAuthSuccess(response: AuthResponse): void {
    this.saveTokens(response.tokens);
    this.saveUserProfile(response.user);
    this.updateAuthState(true);
  }

  /**
   * Verifica se esiste una sessione valida al caricamento dell'app
   * @returns true se esistono token validi
   */
  private hasValidSession(): boolean {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (!accessToken || !refreshToken) {
      return false;
    }

    // Se l'access token è scaduto ma abbiamo un refresh token, consideriamo la sessione valida
    // L'interceptor si occuperà di rinnovare il token automaticamente
    return !this.isTokenExpired(accessToken) || !!refreshToken;
  }

  /**
   * Salva i token nel localStorage
   * @param tokens Token di accesso e refresh
   */
  private saveTokens(tokens: TokenResponse): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
  }

  /**
   * Salva il profilo utente nel localStorage
   * @param user Profilo utente da salvare
   */
  private saveUserProfile(user: UserProfile): void {
    localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
  }

  // ========================================
  // INIZIALIZZAZIONE
  // ========================================

  /**
   * Aggiorna lo stato di autenticazione reattivo
   * @param isAuthenticated Nuovo stato di autenticazione
   */
  private updateAuthState(isAuthenticated: boolean): void {
    this.authStateSubject.next(isAuthenticated);
  }
}
